// Talent Discovery API Service
// Handles search, watchlist, and scout-related functionality

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Helper function to get auth headers (matches api.js pattern)
const getAuthHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  try {
    // Get the current Firebase user
    const { auth } = await import('../../firebaseConfig');
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      // Get the Firebase ID token
      const token = await currentUser.getIdToken();
      // Add BOTH headers (like api.js does)
      headers.Authorization = `Bearer ${token}`;
      headers['X-Auth-Token'] = token;
      return headers;
    }
  } catch (error) {
    // Silent fail - will fallback to localStorage
  }
  
  // Fallback to localStorage if Firebase token not available
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers['X-Auth-Token'] = token;
  }
  
  return headers;
};

// Helper function to handle API responses with better error details
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (parseError) {
      const textError = await response.text().catch(() => '');
      if (textError) {
        errorMessage = textError;
      }
    }
    
    throw new Error(errorMessage);
  }
  return response.json();
};

class TalentDiscoveryService {
  // ============ SEARCH ============

  /**
   * Search for players with advanced filters
   */
  async searchPlayers(filters = {}, options = {}) {
    // Ensure default values for required filters
    const defaultFilters = {
      ageRange: { min: 0, max: 90 },
      performance: {},
      ...filters
    };
    
    const {
      sport,
      position,
      location,
      ageRange,
      availability,
      minSkillLevel,
      performance,
      q
    } = defaultFilters;

    const {
      page = 1,
      limit = 20,
      sortBy = 'relevance'
    } = options;

    const params = new URLSearchParams();

    // Add filters to params - only if they have meaningful values
    if (sport) params.append('sport', sport);
    if (position) params.append('position', position);
    if (location) params.append('location', typeof location === 'string' ? location : JSON.stringify(location));
    
    // Only send ageRange if it's not the default (0-90)
    if (ageRange && (ageRange.min !== 0 || ageRange.max !== 90)) {
      params.append('ageRange', JSON.stringify(ageRange));
    }
    
    if (availability?.length) params.append('availability', availability.join(','));
    if (minSkillLevel) params.append('minSkillLevel', minSkillLevel);
    
    // Only send performance if it has values
    if (performance && Object.keys(performance).length > 0) {
      params.append('performance', JSON.stringify(performance));
    }
    
    // Always send q if provided (text search)
    if (q) {
      params.append('q', q);
    }

    // Add pagination and sorting
    params.append('page', page);
    params.append('limit', limit);
    params.append('sortBy', sortBy);

    try {
      const url = `${API_BASE_URL}/api/talent/search?${params}`;
      let headers = await getAuthHeaders();
      
      let response = await fetch(url, { headers });

      // If unauthorized, try one forced token refresh and retry once
      if (response.status === 401) {
        try {
          const { auth } = await import('../../firebaseConfig');
          const currentUser = auth.currentUser;
          if (currentUser) {
            await currentUser.getIdToken(true); // force refresh
            headers = await getAuthHeaders(); // Get new headers with refreshed token
            response = await fetch(url, { headers }); // Retry the request
          }
        } catch (retryError) {
          // Token refresh failed, continue with original error
        }
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        
        // Try to parse as JSON
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message || errorJson.error || `Server error: ${response.status}`);
        } catch (parseError) {
          throw new Error(errorText || `Server error: ${response.status}`);
        }
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get player recommendations based on club preferences
   */
  async getRecommendations(options = {}) {
    const { limit = 10 } = options;
    const params = new URLSearchParams({ limit });

    const response = await fetch(
      `${API_BASE_URL}/api/talent/recommendations?${params}`,
      {
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  /**
   * Get similar profiles to a reference player
   */
  async getSimilarProfiles(profileId, options = {}) {
    const { limit = 10 } = options;
    const params = new URLSearchParams({ limit });

    const response = await fetch(
      `${API_BASE_URL}/api/talent/similar/${profileId}?${params}`,
      {
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  // ============ WATCHLIST MANAGEMENT ============

  /**
   * Add player to watchlist
   */
  async addToWatchlist(profileId, data = {}) {
    const { notes, tags = [], priority = 'medium' } = data;

    const response = await fetch(
      `${API_BASE_URL}/api/talent/watchlist`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          profileId,
          notes,
          tags,
          priority
        })
      }
    );

    return handleResponse(response);
  }

  /**
   * Get watchlist
   */
  async getWatchlist(filters = {}, options = {}) {
    const { tags, priority, status } = filters;
    const { limit = 50, offset = 0 } = options;

    const params = new URLSearchParams();
    if (tags?.length) params.append('tags', tags.join(','));
    if (priority) params.append('priority', priority);
    if (status) params.append('status', status);
    params.append('limit', limit);
    params.append('offset', offset);

    const url = `${API_BASE_URL}/api/talent/watchlist?${params}`;
    let headers = await getAuthHeaders();

    let response = await fetch(url, { headers });

    // If unauthorized, try one forced token refresh and retry once
    if (response.status === 401) {
      try {
        const { auth } = await import('../../firebaseConfig');
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.getIdToken(true); // force refresh
          headers = await getAuthHeaders();
          response = await fetch(url, { headers });
        }
      } catch (retryError) {
        // Token refresh failed, continue with original error
      }
    }

    return handleResponse(response);
  }

  /**
   * Update watchlist entry
   */
  async updateWatchlistEntry(profileId, updates) {
    const response = await fetch(
      `${API_BASE_URL}/api/talent/watchlist/${profileId}`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(updates)
      }
    );

    return handleResponse(response);
  }

  /**
   * Remove from watchlist
   */
  async removeFromWatchlist(profileId) {
    const response = await fetch(
      `${API_BASE_URL}/api/talent/watchlist/${profileId}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  /**
   * Add contact history entry
   */
  async addContactHistory(profileId, contact) {
    const response = await fetch(
      `${API_BASE_URL}/api/talent/watchlist/${profileId}/contact`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(contact)
      }
    );

    return handleResponse(response);
  }

  // ============ EXPORT ============

  /**
   * Export player profiles
   */
  async exportProfiles(profileIds, format = 'json', sections = []) {
    const response = await fetch(
      `${API_BASE_URL}/api/talent/export`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          profileIds,
          format,
          sections
        })
      }
    );

    return handleResponse(response);
  }

  // ============ COMPARE ============

  /**
   * Compare multiple players
   */
  async comparePlayers(profileIds) {
    const response = await fetch(
      `${API_BASE_URL}/api/talent/compare`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ profileIds })
      }
    );

    return handleResponse(response);
  }
}

const talentDiscoveryService = new TalentDiscoveryService();
export default talentDiscoveryService;
